// Player state sync — keeps season track + skin + streak progress bound to email in Mongo,
// so switching devices or clearing localStorage doesn't wipe your Season Pass rewards.
import axios from "axios";
import { getEmail } from "@/lib/pro";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Snapshot the local progress that's worth persisting. */
function snapshot() {
  const read = (k, def) => {
    try { return JSON.parse(localStorage.getItem(k) || def); } catch { return null; }
  };
  return {
    play_days: read("ppe_season_play_days_v1", "[]"),
    claimed_milestones: read("ppe_season_track_claimed_v1", "[]"),
    season_track_start: localStorage.getItem("ppe_season_track_start_v1") || null,
    current_skin_id: localStorage.getItem("ppe_current_skin_id") || null,
    streak: Number(localStorage.getItem("ppe_streak_count") || 0) || null,
    streak_last_iso: localStorage.getItem("ppe_streak_last") || null,
    achievements: read("ppe_achievements_unlocked", "[]"),
    referral_coupon: localStorage.getItem("ppe_referral_coupon") || null,
    neon_chips: Number(localStorage.getItem("ppe_neon_chips_v1") || 0) || null,
    streak_freezes: Number(localStorage.getItem("ppe_streak_freezes_v1") || 0) || null,
    public_handle: localStorage.getItem("ppe_public_handle_v1") || null,
    handle_gem: localStorage.getItem("ppe_handle_gem_v1") || null,
    duel_streak: Number(localStorage.getItem("ppe_duel_streak_v1") || 0) || null,
  };
}

/** Persist current local state to the server (no-op if no email). */
export async function syncPlayerState(extra = {}) {
  const email = getEmail();
  if (!email) return { skipped: true };
  try {
    const res = await axios.post(`${API}/player/sync`, { email, ...snapshot(), ...extra });
    // Cache server-computed flags so client can gate perks (Gavel Room, etc.)
    try {
      const data = res?.data || {};
      if (typeof data.gavel === "boolean") localStorage.setItem("ppe_gavel_v1", data.gavel ? "1" : "0");
      if (typeof data.duelist === "boolean") localStorage.setItem("ppe_duelist_flag_v1", data.duelist ? "1" : "0");
    } catch {}
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || "sync_failed" };
  }
}

/** Read the cached GAVEL flag (updated on every sync). */
export function hasGavel() {
  try { return localStorage.getItem("ppe_gavel_v1") === "1"; }
  catch { return false; }
}

/** Save an active-run snapshot (for cross-device resume). Called periodically during play. */
export async function saveActiveRun(run) {
  const email = getEmail();
  if (!email) return;
  try {
    await axios.post(`${API}/player/sync`, { email, active_run: { ...run, saved_at: new Date().toISOString() } });
  } catch {}
}

/** Clear the active-run snapshot (called when the run ends). */
export async function clearActiveRun() {
  const email = getEmail();
  if (!email) return;
  try {
    // Send null-empty so server side keeps it stored as-is; simplest: send an empty active_run
    await axios.post(`${API}/player/sync`, { email, active_run: { cleared_at: new Date().toISOString(), level: null } });
  } catch {}
}

/** Fetch server state and hydrate localStorage. Merges play_days/claimed with any local values. */
export async function restorePlayerState() {
  const email = getEmail();
  if (!email) return { skipped: true };
  try {
    const res = await axios.get(`${API}/player/restore/${encodeURIComponent(email)}`);
    if (!res.data?.found) return { found: false };
    const d = res.data;
    const set = (k, v) => v != null && localStorage.setItem(k, v);
    const setJson = (k, v) => v && localStorage.setItem(k, JSON.stringify(v));
    // Union play days + claimed milestones so no progress is lost
    const localPlays = JSON.parse(localStorage.getItem("ppe_season_play_days_v1") || "[]");
    const mergedPlays = Array.from(new Set([...(d.play_days || []), ...localPlays]));
    setJson("ppe_season_play_days_v1", mergedPlays);
    const localClaimed = JSON.parse(localStorage.getItem("ppe_season_track_claimed_v1") || "[]");
    const mergedClaimed = Array.from(new Set([...(d.claimed_milestones || []), ...localClaimed]));
    setJson("ppe_season_track_claimed_v1", mergedClaimed);
    if (d.season_track_start && !localStorage.getItem("ppe_season_track_start_v1")) {
      localStorage.setItem("ppe_season_track_start_v1", d.season_track_start);
    }
    set("ppe_current_skin_id", d.current_skin_id);
    if (typeof d.streak === "number") localStorage.setItem("ppe_streak_count", String(d.streak));
    set("ppe_streak_last", d.streak_last_iso);
    setJson("ppe_achievements_unlocked", d.achievements);
    if (typeof d.neon_chips === "number") {
      const local = Number(localStorage.getItem("ppe_neon_chips_v1") || 0);
      localStorage.setItem("ppe_neon_chips_v1", String(Math.max(local, d.neon_chips)));
    }
    if (typeof d.streak_freezes === "number") {
      const local = Number(localStorage.getItem("ppe_streak_freezes_v1") || 0);
      localStorage.setItem("ppe_streak_freezes_v1", String(Math.max(local, d.streak_freezes)));
    }
    if (d.referral_coupon && !localStorage.getItem("ppe_referral_coupon")) {
      localStorage.setItem("ppe_referral_coupon", d.referral_coupon);
    }
    return { found: true };
  } catch {
    return { found: false, error: true };
  }
}
